import React, { useRef, useEffect } from 'react';
import { setupCanvas } from './canvasUtils';

// ============================================
// Chapter 1  -  The Building Blocks of the Universe
// ============================================

/** 1.1 – Scales of Matter (human → quark) */
export const MatterScaleLadder = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 580, H = 260;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        const items = [
            { label: 'Human', scale: '~1 m', color: '#3B82F6', sym: 'H' },
            { label: 'Cell', scale: '~10 μm', color: '#22C55E', sym: 'C' },
            { label: 'Atom', scale: '~0.1 nm', color: '#F59E0B', sym: 'A' },
            { label: 'Nucleus', scale: '~1 fm', color: '#EF4444', sym: 'N' },
            { label: 'Quark', scale: '<10⁻¹⁸ m', color: '#8B5CF6', sym: 'q' },
        ];
        const sx = 60, gap = 120, cy = 110, r = 30;

        // Title
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Scales of Matter', W / 2, 26);

        // Arrows between circles
        for (let i = 0; i < items.length - 1; i++) {
            const x1 = sx + i * gap + r + 6, x2 = sx + (i + 1) * gap - r - 6;
            ctx.strokeStyle = '#CBD5E1'; ctx.lineWidth = 2;
            ctx.setLineDash([4, 3]); ctx.beginPath(); ctx.moveTo(x1, cy); ctx.lineTo(x2, cy); ctx.stroke(); ctx.setLineDash([]);
            ctx.fillStyle = '#CBD5E1'; ctx.beginPath(); ctx.moveTo(x2, cy); ctx.lineTo(x2 - 7, cy - 4); ctx.lineTo(x2 - 7, cy + 4); ctx.closePath(); ctx.fill();
            ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText('�-10⁵', (x1 + x2) / 2, cy - 12);
        }
        // Circles
        items.forEach((it, i) => {
            const x = sx + i * gap;
            ctx.beginPath(); ctx.arc(x, cy, r, 0, Math.PI * 2);
            ctx.fillStyle = it.color + '18'; ctx.fill(); ctx.strokeStyle = it.color; ctx.lineWidth = 2.5; ctx.stroke();
            ctx.fillStyle = it.color; ctx.font = 'bold 20px Georgia,serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(it.sym, x, cy);
            ctx.textBaseline = 'alphabetic';
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.fillText(it.label, x, cy + r + 20);
            ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.fillText(it.scale, x, cy + r + 36);
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Each step zooms in by roughly 5 orders of magnitude  -  spanning 18 in total', W / 2, H - 12);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={580} height={260} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 1.4 – The Six Leptons (3 generations �- 2 rows) */
export const LeptonTable = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 16px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('The Six Leptons', W / 2, 26);

        const leptons = [
            { name: 'Electron', sym: 'e⁻', q: '−1', mass: '0.511 MeV', col: '#8B5CF6', g: 0 },
            { name: 'Muon', sym: 'μ⁻', q: '−1', mass: '105.7 MeV', col: '#7C3AED', g: 1 },
            { name: 'Tau', sym: 'τ⁻', q: '−1', mass: '1777 MeV', col: '#6D28D9', g: 2 },
            { name: 'e Neutrino', sym: 'νₑ', q: '0', mass: '< 1 eV', col: '#A78BFA', g: 0 },
            { name: 'μ Neutrino', sym: 'νμ', q: '0', mass: '< 0.17 MeV', col: '#8B5CF6', g: 1 },
            { name: 'τ Neutrino', sym: 'ντ', q: '0', mass: '< 18.2 MeV', col: '#7C3AED', g: 2 },
        ];

        ['Gen I', 'Gen II', 'Gen III'].forEach((l, i) => {
            ctx.fillStyle = '#64748B'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(l, 95 + i * 170, 52);
        });

        leptons.forEach(l => {
            const row = l.q === '−1' ? 0 : 1;
            const x = 15 + l.g * 170, y = 64 + row * 118;
            const cw = 155, ch = 100;
            ctx.fillStyle = 'white'; ctx.shadowColor = 'rgba(0,0,0,0.06)'; ctx.shadowBlur = 8; ctx.shadowOffsetY = 2;
            ctx.beginPath(); ctx.roundRect(x, y, cw, ch, 12); ctx.fill(); ctx.shadowBlur = 0;
            ctx.fillStyle = l.col; ctx.beginPath(); ctx.roundRect(x, y, cw, 5, [12, 12, 0, 0]); ctx.fill();

            ctx.beginPath(); ctx.arc(x + 38, y + 52, 22, 0, Math.PI * 2);
            ctx.fillStyle = l.col + '15'; ctx.fill();
            ctx.fillStyle = l.col; ctx.font = 'bold 20px Georgia,serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(l.sym, x + 38, y + 52);
            ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.fillText(l.name, x + 68, y + 40);
            ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
            ctx.fillText('Q: ' + l.q, x + 68, y + 58); ctx.fillText('m: ' + l.mass, x + 68, y + 74);
        });
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-purple-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={300} className="mx-auto block rounded-xl" style={{ maxWidth: '100%' }} />
            <p className="text-center text-sm text-slate-500 mt-3">Leptons interact via the electromagnetic and weak forces  -  never the strong force</p>
        </div>
    );
};

/** 1.5 – Rutherford Gold Foil Scattering */
export const RutherfordScattering = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 340;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Rutherford Scattering Experiment', W / 2, 24);

        const nx = 320, ny = 160;
        // Gold nucleus
        ctx.beginPath(); ctx.arc(nx, ny, 28, 0, Math.PI * 2);
        const ng = ctx.createRadialGradient(nx - 5, ny - 5, 0, nx, ny, 28);
        ng.addColorStop(0, '#FCD34D'); ng.addColorStop(1, '#B45309');
        ctx.fillStyle = ng; ctx.fill();
        ctx.fillStyle = 'white'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.fillText('Au', nx, ny + 5);
        ctx.fillStyle = '#92400E'; ctx.font = '11px Inter,sans-serif'; ctx.fillText('Gold Nucleus', nx, ny + 48);

        // Alpha source
        ctx.fillStyle = '#E2E8F0'; ctx.beginPath(); ctx.roundRect(20, ny - 35, 80, 70, 8); ctx.fill();
        ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(20, ny - 35, 80, 70, 8); ctx.stroke();
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('α Source', 60, ny - 2); ctx.fillText('(²¹⁰Po)', 60, ny + 14);

        // Alpha tracks
        const tracks = [
            { endX: 500, endY: ny - 5, dash: false, label: 'Passes through', col: '#3B82F6' },
            { endX: 490, endY: ny + 20, dash: false, label: '', col: '#3B82F6' },
            { endX: 460, endY: ny - 60, dash: false, label: 'Small deflection', col: '#F59E0B' },
            { endX: 430, endY: ny + 80, dash: false, label: '', col: '#F59E0B' },
            { endX: 140, endY: ny - 90, dash: false, label: 'Back-scatter!', col: '#EF4444' },
        ];
        tracks.forEach(t => {
            ctx.strokeStyle = t.col; ctx.lineWidth = 2;
            ctx.beginPath(); ctx.moveTo(100, ny);
            // Path bends near nucleus
            if (t.endX < 200) {
                ctx.quadraticCurveTo(nx - 50, ny - 20, t.endX, t.endY);
            } else if (t.endY !== ny - 5 && t.endY !== ny + 20) {
                ctx.quadraticCurveTo(nx - 40, ny, t.endX, t.endY);
            } else {
                ctx.lineTo(t.endX, t.endY);
            }
            ctx.stroke();
            // Arrow
            if (t.label) {
                ctx.fillStyle = t.col; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'left';
                ctx.fillText(t.label, t.endX + (t.endX < 200 ? -70 : 5), t.endY - 8);
            }
        });

        // Stats
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Most alphas pass straight through  -  the atom is mostly empty space', W / 2, H - 40);
        ctx.fillStyle = '#EF4444'; ctx.font = 'bold 12px Inter,sans-serif';
        ctx.fillText('1 in ~8000 scatter backward → tiny, dense nucleus!', W / 2, H - 18);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-amber-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={340} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 1.10 – Standard Model Family Tree */
export const ParticleFamilyTree = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 580, H = 320;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        const drawNode = (x, y, text, color, w = 90, h = 30) => {
            ctx.fillStyle = color + '18'; ctx.beginPath(); ctx.roundRect(x - w / 2, y - h / 2, w, h, 8); ctx.fill();
            ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(x - w / 2, y - h / 2, w, h, 8); ctx.stroke();
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(text, x, y);
        };
        const drawLine = (x1, y1, x2, y2) => {
            ctx.strokeStyle = '#CBD5E1'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
        };

        // Root
        drawNode(W / 2, 28, 'Standard Model', '#1E293B', 130, 32);

        // Level 1: Fermions / Bosons
        drawLine(W / 2 - 30, 44, 190, 72); drawLine(W / 2 + 30, 44, 420, 72);
        drawNode(190, 82, 'Fermions', '#3B82F6', 100, 28);
        drawNode(420, 82, 'Bosons', '#F59E0B', 100, 28);

        // Level 2: Quarks / Leptons / Gauge / Higgs
        drawLine(190 - 20, 96, 110, 128); drawLine(190 + 20, 96, 280, 128);
        drawNode(110, 138, 'Quarks', '#22C55E', 90, 26);
        drawNode(280, 138, 'Leptons', '#8B5CF6', 90, 26);
        drawLine(420 - 20, 96, 370, 128); drawLine(420 + 20, 96, 490, 128);
        drawNode(370, 138, 'Gauge', '#F59E0B', 80, 26);
        drawNode(490, 138, 'Higgs', '#EC4899', 70, 26);

        // Level 3: Individual particles
        const quarks = ['u', 'd', 'c', 's', 't', 'b'];
        const leptons = ['e⁻', 'νₑ', 'μ⁻', 'νμ', 'τ⁻', 'ντ'];
        const gauge = ['γ', 'g', 'W±', 'Z⁰'];

        quarks.forEach((q, i) => {
            const x = 38 + i * 26;
            drawLine(110, 151, x, 172);
            ctx.beginPath(); ctx.arc(x, 185, 14, 0, Math.PI * 2);
            ctx.fillStyle = '#22C55E20'; ctx.fill(); ctx.strokeStyle = '#22C55E'; ctx.lineWidth = 1.5; ctx.stroke();
            ctx.fillStyle = '#22C55E'; ctx.font = 'bold 13px Georgia,serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(q, x, 185);
        });
        leptons.forEach((l, i) => {
            const x = 210 + i * 26;
            drawLine(280, 151, x, 172);
            ctx.beginPath(); ctx.arc(x, 185, 14, 0, Math.PI * 2);
            ctx.fillStyle = '#8B5CF620'; ctx.fill(); ctx.strokeStyle = '#8B5CF6'; ctx.lineWidth = 1.5; ctx.stroke();
            ctx.fillStyle = '#8B5CF6'; ctx.font = 'bold 11px Georgia,serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(l, x, 185);
        });
        gauge.forEach((b, i) => {
            const x = 340 + i * 28;
            drawLine(370, 151, x, 172);
            ctx.beginPath(); ctx.arc(x, 185, 14, 0, Math.PI * 2);
            ctx.fillStyle = '#F59E0B20'; ctx.fill(); ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = 1.5; ctx.stroke();
            ctx.fillStyle = '#D97706'; ctx.font = 'bold 11px Georgia,serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(b, x, 185);
        });
        // Higgs
        drawLine(490, 151, 490, 172);
        ctx.beginPath(); ctx.arc(490, 185, 14, 0, Math.PI * 2);
        ctx.fillStyle = '#EC489920'; ctx.fill(); ctx.strokeStyle = '#EC4899'; ctx.lineWidth = 1.5; ctx.stroke();
        ctx.fillStyle = '#EC4899'; ctx.font = 'bold 13px Georgia,serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('H', 490, 185);

        // Generation labels
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif';
        ctx.fillText('6 flavors, 3 colors each', 110, 218);
        ctx.fillText('6 types, 3 generations', 280, 218);
        ctx.fillText('Force carriers', 370, 218);

        // Bottom legend
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('17 fundamental particles + their antiparticles make up all known matter and forces', W / 2, H - 50);

        // Generation bracket
        ctx.strokeStyle = '#CBD5E1'; ctx.lineWidth = 1;
        const genLabels = ['I', 'II', 'III'];
        for (let g = 0; g < 3; g++) {
            const gx = 38 + g * 52, gw = 40;
            ctx.beginPath(); ctx.moveTo(gx - 5, 205); ctx.lineTo(gx - 5, 212); ctx.lineTo(gx + gw, 212); ctx.lineTo(gx + gw, 205); ctx.stroke();
            ctx.fillStyle = '#94A3B8'; ctx.font = '9px Inter,sans-serif';
            ctx.fillText(genLabels[g], gx + gw / 2 - 2, 224);
        }

    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-indigo-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={580} height={320} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};


// ============================================
// Chapter 2  -  Forces of Nature
// ============================================

/** 2.2 – Gravity: Force Strength Comparison (log scale) */
export const GravityScaleComparison = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Relative Force Strengths (log scale)', W / 2, 26);

        const forces = [
            { name: 'Strong Nuclear', strength: 1, log: 0, color: '#22C55E', carrier: 'Gluon (g)' },
            { name: 'Electromagnetic', strength: 1 / 137, log: -2.14, color: '#F59E0B', carrier: 'Photon (γ)' },
            { name: 'Weak Nuclear', strength: 1e-6, log: -6, color: '#3B82F6', carrier: 'W±, Z⁰' },
            { name: 'Gravity', strength: 6e-39, log: -38.2, color: '#8B5CF6', carrier: 'Graviton (?)' },
        ];

        const barX = 180, barMaxW = 340, barH = 32, startY = 55;
        const maxLog = 0, minLog = -40;

        forces.forEach((f, i) => {
            const y = startY + i * 55;
            const barW = Math.max(8, ((f.log - minLog) / (maxLog - minLog)) * barMaxW);

            // Label left
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'right';
            ctx.fillText(f.name, barX - 12, y + 14);
            ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif';
            ctx.fillText(f.carrier, barX - 12, y + 28);

            // Bar
            ctx.fillStyle = f.color + '25'; ctx.beginPath(); ctx.roundRect(barX, y, barW, barH, 6); ctx.fill();
            ctx.fillStyle = f.color; ctx.beginPath(); ctx.roundRect(barX, y, barW, barH, 6); ctx.fill();
            ctx.globalAlpha = 0.3; ctx.fillStyle = f.color; ctx.beginPath(); ctx.roundRect(barX, y, barW, barH, 6); ctx.fill();
            ctx.globalAlpha = 1;
            ctx.fillStyle = f.color; ctx.beginPath(); ctx.roundRect(barX, y, barW, barH, 6); ctx.fill();

            // Strength label
            ctx.fillStyle = 'white'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'left';
            if (barW > 50) ctx.fillText(f.log === 0 ? '1' : '10' + (f.log < -9 ? '⁻³⁹' : f.log === -6 ? '⁻⁶' : '⁻²'), barX + 8, y + 20);
            else {
                ctx.fillStyle = f.color; ctx.fillText(f.log === 0 ? '1' : '10' + (f.log < -9 ? '⁻³⁹' : f.log === -6 ? '⁻⁶' : '⁻²'), barX + barW + 6, y + 20);
            }
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Gravity is 10³⁹ times weaker than the strong force  -  yet it shapes the cosmos', W / 2, H - 12);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-emerald-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 2.3 – Electromagnetic Force: Photon Exchange */
export const EMForceInteraction = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 520, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Electromagnetic Force: Photon Exchange', W / 2, 24);

        // Two charges
        const q1x = 140, q2x = 380, qy = 130, qr = 30;

        // Field lines (repulsive, both +)
        ctx.strokeStyle = '#FDE68A'; ctx.lineWidth = 1;
        for (let a = -0.6; a <= 0.6; a += 0.3) {
            ctx.beginPath();
            ctx.moveTo(q1x + qr * Math.cos(a), qy + qr * Math.sin(a));
            ctx.quadraticCurveTo(W / 2, qy + a * 100, q2x - qr * Math.cos(a), qy + qr * Math.sin(a));
            ctx.stroke();
        }

        // Virtual photon exchange (wavy line)
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 2.5;
        ctx.beginPath();
        for (let x = q1x + qr + 5; x <= q2x - qr - 5; x += 2) {
            const progress = (x - q1x - qr) / (q2x - q1x - 2 * qr);
            const wave = Math.sin(progress * Math.PI * 8) * 12;
            if (x === q1x + qr + 5) ctx.moveTo(x, qy + wave);
            else ctx.lineTo(x, qy + wave);
        }
        ctx.stroke();
        ctx.fillStyle = '#EF4444'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('γ (virtual photon)', W / 2, qy - 22);

        // Charge 1
        ctx.beginPath(); ctx.arc(q1x, qy, qr, 0, Math.PI * 2);
        const g1 = ctx.createRadialGradient(q1x - 5, qy - 5, 0, q1x, qy, qr);
        g1.addColorStop(0, '#60A5FA'); g1.addColorStop(1, '#2563EB');
        ctx.fillStyle = g1; ctx.fill();
        ctx.fillStyle = 'white'; ctx.font = 'bold 22px Inter,sans-serif'; ctx.fillText('+', q1x, qy + 8);

        // Charge 2
        ctx.beginPath(); ctx.arc(q2x, qy, qr, 0, Math.PI * 2);
        const g2 = ctx.createRadialGradient(q2x - 5, qy - 5, 0, q2x, qy, qr);
        g2.addColorStop(0, '#60A5FA'); g2.addColorStop(1, '#2563EB');
        ctx.fillStyle = g2; ctx.fill();
        ctx.fillStyle = 'white'; ctx.font = 'bold 22px Inter,sans-serif'; ctx.fillText('+', q2x, qy + 8);

        // Repulsion arrows
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 20px Inter,sans-serif';
        ctx.fillText('←', q1x - qr - 20, qy + 7); ctx.fillText('→', q2x + qr + 8, qy + 7);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Repulsion', q1x - qr - 16, qy + 24); ctx.fillText('Repulsion', q2x + qr + 14, qy + 24);

        // Labels
        ctx.fillStyle = '#1E293B'; ctx.font = '13px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Proton (+e)', q1x, qy + qr + 22); ctx.fillText('Proton (+e)', q2x, qy + qr + 22);

        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
        ctx.fillText('Like charges repel via virtual photon exchange', W / 2, H - 30);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('F = k·q₁q₂/r²   |   Coupling constant α ≈ 1/137', W / 2, H - 12);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-amber-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={520} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 2.4 – Color Confinement in QCD */
export const ColorConfinement = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Color Confinement: Quarks Inside a Proton', W / 2, 24);

        // Proton boundary (left side)
        const px = 160, py = 155, pr = 75;
        ctx.beginPath(); ctx.arc(px, py, pr, 0, Math.PI * 2);
        ctx.fillStyle = '#F0FDF4'; ctx.fill();
        ctx.strokeStyle = '#86EFAC'; ctx.lineWidth = 2; ctx.setLineDash([6, 4]); ctx.stroke(); ctx.setLineDash([]);
        ctx.fillStyle = '#22C55E'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Proton (uud)', px, py + pr + 18);

        // Three quarks
        const quarks = [
            { x: px - 30, y: py - 30, color: '#EF4444', label: 'u (Red)' },
            { x: px + 35, y: py - 20, color: '#22C55E', label: 'u (Green)' },
            { x: px, y: py + 35, color: '#3B82F6', label: 'd (Blue)' },
        ];

        // Gluon springs between quarks
        quarks.forEach((q1, i) => {
            const q2 = quarks[(i + 1) % 3];
            ctx.strokeStyle = '#FCD34D'; ctx.lineWidth = 2.5;
            ctx.beginPath();
            const steps = 20;
            for (let s = 0; s <= steps; s++) {
                const t = s / steps;
                const mx = q1.x + (q2.x - q1.x) * t;
                const my = q1.y + (q2.y - q1.y) * t;
                const wave = Math.sin(t * Math.PI * 4) * 6;
                const nx = -(q2.y - q1.y), ny = q2.x - q1.x;
                const len = Math.sqrt(nx * nx + ny * ny);
                const px2 = mx + (nx / len) * wave, py2 = my + (ny / len) * wave;
                if (s === 0) ctx.moveTo(px2, py2); else ctx.lineTo(px2, py2);
            }
            ctx.stroke();
        });

        // Quark circles
        quarks.forEach(q => {
            ctx.beginPath(); ctx.arc(q.x, q.y, 16, 0, Math.PI * 2);
            ctx.fillStyle = q.color; ctx.fill();
            ctx.fillStyle = 'white'; ctx.font = 'bold 14px Georgia,serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(q.label[0], q.x, q.y);
        });

        // Right side: confinement illustration
        const rx = 410, ry = 130;
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
        ctx.fillText('Trying to pull apart:', rx, ry - 50);

        // Two quarks being pulled apart with string
        ctx.beginPath(); ctx.arc(rx - 50, ry, 12, 0, Math.PI * 2); ctx.fillStyle = '#EF4444'; ctx.fill();
        ctx.beginPath(); ctx.arc(rx + 50, ry, 12, 0, Math.PI * 2); ctx.fillStyle = '#3B82F6'; ctx.fill();
        ctx.fillStyle = 'white'; ctx.font = 'bold 10px Georgia,serif'; ctx.textBaseline = 'middle';
        ctx.fillText('q', rx - 50, ry); ctx.fillText('q̄', rx + 50, ry);

        // Stretching string
        ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = 3;
        ctx.beginPath();
        for (let x = rx - 38; x <= rx + 38; x += 2) {
            const t = (x - rx + 38) / 76;
            const wave = Math.sin(t * Math.PI * 6) * 5;
            if (x === rx - 38) ctx.moveTo(x, ry + wave); else ctx.lineTo(x, ry + wave);
        }
        ctx.stroke();

        // Arrow: string breaks → new pair
        ctx.fillStyle = '#475569'; ctx.font = '13px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('↓ String breaks ↓', rx, ry + 35);

        // New pair created
        ctx.beginPath(); ctx.arc(rx - 40, ry + 60, 10, 0, Math.PI * 2); ctx.fillStyle = '#EF4444'; ctx.fill();
        ctx.beginPath(); ctx.arc(rx - 15, ry + 60, 10, 0, Math.PI * 2); ctx.fillStyle = '#3B82F620'; ctx.fill(); ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 1.5; ctx.stroke();
        ctx.beginPath(); ctx.arc(rx + 15, ry + 60, 10, 0, Math.PI * 2); ctx.fillStyle = '#EF444420'; ctx.fill(); ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 1.5; ctx.stroke();
        ctx.beginPath(); ctx.arc(rx + 40, ry + 60, 10, 0, Math.PI * 2); ctx.fillStyle = '#3B82F6'; ctx.fill();
        ctx.fillStyle = 'white'; ctx.font = 'bold 9px Georgia,serif'; ctx.textBaseline = 'middle';
        ctx.fillText('q', rx - 40, ry + 60); ctx.fillText('q̄', rx - 15, ry + 60);
        ctx.fillText('q', rx + 15, ry + 60); ctx.fillText('q̄', rx + 40, ry + 60);

        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
        ctx.fillText('New qq̄ pair created!', rx, ry + 90);
        ctx.fillText('→ You never get a free quark', rx, ry + 106);

        // Bottom
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Quarks are permanently confined  -  free quarks have never been observed', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-green-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 2.5 – Weak Force: Beta Decay Feynman Diagram */
export const WeakDecayDiagram = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 520, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Beta Decay via the Weak Force', W / 2, 24);

        const cx = W / 2, cy = 150;

        // Neutron (incoming, left)
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 2.5;
        ctx.beginPath(); ctx.moveTo(80, cy + 50); ctx.lineTo(cx - 20, cy); ctx.stroke();
        // Arrow
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 13px Georgia,serif'; ctx.textAlign = 'right';
        ctx.fillText('n', 75, cy + 55);

        // Proton (outgoing, top-left)
        ctx.strokeStyle = '#22C55E'; ctx.lineWidth = 2.5;
        ctx.beginPath(); ctx.moveTo(cx - 20, cy); ctx.lineTo(80, cy - 60); ctx.stroke();
        ctx.fillStyle = '#22C55E'; ctx.font = 'bold 13px Georgia,serif';
        ctx.fillText('p', 75, cy - 55);

        // W⁻ boson (wavy line, horizontal)
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 2.5;
        ctx.beginPath();
        for (let x = cx - 20; x <= cx + 80; x += 2) {
            const t = (x - cx + 20) / 100;
            const wave = Math.sin(t * Math.PI * 7) * 10;
            if (x === cx - 20) ctx.moveTo(x, cy + wave); else ctx.lineTo(x, cy + wave);
        }
        ctx.stroke();
        ctx.fillStyle = '#EF4444'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('W⁻', cx + 30, cy - 18);

        // Vertex dots
        ctx.fillStyle = '#1E293B';
        ctx.beginPath(); ctx.arc(cx - 20, cy, 5, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(cx + 80, cy, 5, 0, Math.PI * 2); ctx.fill();

        // Electron (outgoing, bottom-right)
        ctx.strokeStyle = '#8B5CF6'; ctx.lineWidth = 2.5;
        ctx.beginPath(); ctx.moveTo(cx + 80, cy); ctx.lineTo(440, cy + 50); ctx.stroke();
        ctx.fillStyle = '#8B5CF6'; ctx.font = 'bold 13px Georgia,serif'; ctx.textAlign = 'left';
        ctx.fillText('e⁻', 445, cy + 55);

        // Antineutrino (outgoing, top-right)
        ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = 2; ctx.setLineDash([4, 3]);
        ctx.beginPath(); ctx.moveTo(cx + 80, cy); ctx.lineTo(440, cy - 60); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#F59E0B'; ctx.font = 'bold 13px Georgia,serif';
        ctx.fillText('ν̄ₑ', 445, cy - 55);

        // Equation
        ctx.fillStyle = '#475569'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('n → p + e⁻ + ν̄ₑ', W / 2, H - 32);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('The W⁻ boson mediates the weak interaction, changing quark flavor: d → u', W / 2, H - 12);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-red-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={520} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 2.6 – Beta Decay Historical Timeline */
export const BetaDecayTimeline = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 220;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Road to Understanding Beta Decay', W / 2, 24);

        const events = [
            { year: '1930', title: 'Pauli', desc: 'Proposes the neutrino\nto save energy conservation', color: '#3B82F6' },
            { year: '1934', title: 'Fermi', desc: 'Four-fermion theory\nof weak interaction', color: '#F59E0B' },
            { year: '1956', title: 'Cowan & Reines', desc: 'First neutrino detected\nat Savannah River reactor', color: '#22C55E' },
            { year: '1983', title: 'Rubbia et al.', desc: 'W and Z bosons found\nat CERN SppS collider', color: '#8B5CF6' },
        ];

        const lineY = 110, sx = 60, gap = 130;

        // Timeline line
        ctx.strokeStyle = '#E2E8F0'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(sx - 20, lineY); ctx.lineTo(sx + (events.length - 1) * gap + 20, lineY); ctx.stroke();

        events.forEach((e, i) => {
            const x = sx + i * gap;
            // Dot
            ctx.beginPath(); ctx.arc(x, lineY, 8, 0, Math.PI * 2);
            ctx.fillStyle = e.color; ctx.fill();
            ctx.beginPath(); ctx.arc(x, lineY, 4, 0, Math.PI * 2);
            ctx.fillStyle = 'white'; ctx.fill();

            // Year above
            ctx.fillStyle = e.color; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(e.year, x, lineY - 20);

            // Title
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif';
            ctx.fillText(e.title, x, lineY + 28);

            // Description (multi-line)
            ctx.fillStyle = '#64748B'; ctx.font = '10px Inter,sans-serif';
            e.desc.split('\n').forEach((line, li) => {
                ctx.fillText(line, x, lineY + 44 + li * 14);
            });
        });
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-amber-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={220} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 2.10 – Force Unification Energy Pyramid */
export const ForceUnificationPyramid = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 520, H = 320;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Force Unification at Higher Energies', W / 2, 24);

        // Pyramid (inverted  -  wider at top = higher energy)
        const levels = [
            { y: 55, w: 440, label: 'Theory of Everything?', energy: '~10¹⁹ GeV (Planck)', color: '#8B5CF6', forces: 'All 4 forces unified' },
            { y: 110, w: 380, label: 'Grand Unified Theory', energy: '~10¹⁵ GeV', color: '#3B82F6', forces: 'Strong + Electroweak' },
            { y: 165, w: 300, label: 'Electroweak Unification', energy: '~100 GeV', color: '#F59E0B', forces: 'EM + Weak unified' },
            { y: 220, w: 200, label: 'Everyday Physics', energy: '< 1 GeV', color: '#22C55E', forces: '4 separate forces' },
        ];

        levels.forEach(l => {
            const x = (W - l.w) / 2;
            ctx.fillStyle = l.color + '15';
            ctx.beginPath(); ctx.roundRect(x, l.y, l.w, 42, 8); ctx.fill();
            ctx.strokeStyle = l.color; ctx.lineWidth = 2;
            ctx.beginPath(); ctx.roundRect(x, l.y, l.w, 42, 8); ctx.stroke();

            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(l.label, W / 2, l.y + 18);
            ctx.fillStyle = '#64748B'; ctx.font = '10px Inter,sans-serif';
            ctx.fillText(l.forces + '  ·  ' + l.energy, W / 2, l.y + 34);
        });

        // Connecting lines
        ctx.strokeStyle = '#CBD5E1'; ctx.lineWidth = 1.5; ctx.setLineDash([3, 3]);
        for (let i = 0; i < levels.length - 1; i++) {
            ctx.beginPath(); ctx.moveTo(W / 2, levels[i].y + 42); ctx.lineTo(W / 2, levels[i + 1].y); ctx.stroke();
        }
        ctx.setLineDash([]);

        // Energy arrow on right
        ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(W - 30, 250); ctx.lineTo(W - 30, 55); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(W - 30, 55); ctx.lineTo(W - 35, 65); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(W - 30, 55); ctx.lineTo(W - 25, 65); ctx.stroke();
        ctx.fillStyle = '#94A3B8'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.save(); ctx.translate(W - 15, 155); ctx.rotate(-Math.PI / 2); ctx.fillText('Energy →', 0, 0); ctx.restore();

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('At higher energies, forces merge  -  confirmed for EM + Weak at ~100 GeV', W / 2, H - 12);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-violet-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={520} height={320} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};
